import { ReactNode } from 'react';
import {
  Tabs as AriaTabs,
  TabList,
  Tab,
  type Key,
} from 'react-aria-components';
import { cx } from './cx';
import { typography } from './Text';

export interface TabItem {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Optional trailing count, rendered as a small mono badge. */
  count?: number;
}

export interface TabsProps {
  items: TabItem[];
  value: string;
  onChange: (value: string) => void;
  'aria-label'?: string;
  className?: string;
}

/**
 * Segmented-control tab bar — the Untitled UI idiom: a muted track holding the
 * tabs, the selected one lifted onto a surface chip with an xs shadow.
 * react-aria `Tabs` provides roving focus + ARIA.
 */
export const Tabs = ({
  items,
  value,
  onChange,
  className,
  ...rest
}: TabsProps) => (
  <AriaTabs
    selectedKey={value}
    onSelectionChange={(key: Key) => onChange(String(key))}
    className={className}
  >
    <TabList
      aria-label={rest['aria-label']}
      items={items}
      className="inline-flex items-center gap-1 rounded-lg border border-default bg-muted-strong p-1"
    >
      {(item: TabItem) => (
        <Tab
          id={item.value}
          className={cx(
            typography.button,
            'inline-flex cursor-pointer items-center gap-1.5 rounded-md px-3 py-1.5 outline-none transition',
            'text-fg-tertiary hover:text-fg-secondary',
            'data-[focus-visible]:ring-4 data-[focus-visible]:ring-[rgb(var(--c-ring)/0.24)]',
            'selected:bg-surface selected:text-fg selected:shadow-xs',
          )}
        >
          {item.icon}
          {item.label}
          {typeof item.count === 'number' && (
            <span
              className={cx(
                typography.caption,
                typography.mono,
                'rounded-full bg-muted px-1.5 font-semibold text-fg-tertiary group-selected:text-brand-text',
              )}
            >
              {item.count}
            </span>
          )}
        </Tab>
      )}
    </TabList>
  </AriaTabs>
);
